import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { BookStack, Brain, CheckCircle, SkipNext, Tools } from 'iconoir-react';
import { Bot } from 'lucide-react';
import type { StreamStatusEntry, StreamStatusEntryIcon } from '../types/chat';
import { sortStreamStatusEntriesForDisplay } from '../utils/stream-status-entries';
import { StreamTodoList } from './StreamTodoList';
import { cn } from 'lib/utils';

const STATUS_ICON_SIZE = 20;
const STATUS_ICON_STROKE = 1.5;
/** Delay between revealing each status row (started → finished → todos). */
const REVEAL_STAGGER_MS = 220;
/** Visible log rows before credits-style scroll + top fade kicks in. */
const MAX_VISIBLE_LOG_ROWS = 5;
const statusIcons: Record<StreamStatusEntryIcon, typeof Brain | typeof Bot> = {
  skill_used: Brain,
  skill_resource: BookStack,
  skill_skipped: SkipNext,
  tool_started: Tools,
  tool_completed: CheckCircle,
  subagent_started: Bot,
  subagent_completed: Bot,
  processing_started: Brain,
};

function StreamStatusIcon({ icon }: { readonly icon: StreamStatusEntryIcon }) {
  const Icon = statusIcons[icon];

  if (icon === 'subagent_started' || icon === 'subagent_completed') {
    return (
      <Bot
        width={STATUS_ICON_SIZE}
        height={STATUS_ICON_SIZE}
        strokeWidth={STATUS_ICON_STROKE}
        className="shrink-0 text-[#6D6D6D] dark:text-muted-foreground"
      />
    );
  }

  return (
    <Icon
      width={STATUS_ICON_SIZE}
      height={STATUS_ICON_SIZE}
      strokeWidth={STATUS_ICON_STROKE}
      className="shrink-0 text-[#6D6D6D] dark:text-muted-foreground"
    />
  );
}

interface StreamStatusTimelineProps {
  entries?: StreamStatusEntry[];
  className?: string;
  /** Shimmer the active (last) row — used in normal chat; agent chat keeps static labels. */
  shimmerActiveRow?: boolean;
  /** Reveal log rows + todos one after another instead of all at once. */
  staggerReveal?: boolean;
  /** Max status rows shown before older rows scroll up with a top fade. */
  maxVisibleLogRows?: number;
}

export function StreamStatusTimeline({
  entries,
  className,
  shimmerActiveRow = true,
  staggerReveal = true,
  maxVisibleLogRows = MAX_VISIBLE_LOG_ROWS,
}: Readonly<StreamStatusTimelineProps>) {
  const orderedEntries = sortStreamStatusEntriesForDisplay(entries ?? []);
  const logEntries = orderedEntries.filter((entry) => !entry.todos?.length);
  const todoEntry = orderedEntries.find((entry) => entry.todos?.length);
  const targetCount = logEntries.length + (todoEntry?.todos?.length ? 1 : 0);
  const logViewportRef = useRef<HTMLDivElement>(null);
  const logListRef = useRef<HTMLOListElement>(null);
  const prevLastVisibleLogIdRef = useRef<string | undefined>(undefined);
  const userPinnedToBottomRef = useRef(true);

  const [revealedCount, setRevealedCount] = useState(() =>
    staggerReveal ? Math.min(1, targetCount) : targetCount,
  );
  const [todosRevealed, setTodosRevealed] = useState(false);
  const targetRef = useRef(targetCount);
  targetRef.current = targetCount;

  useEffect(() => {
    if (!staggerReveal) {
      setRevealedCount(targetCount);
      return;
    }

    // Shrink if entries were removed (rare).
    if (revealedCount > targetCount) {
      setRevealedCount(targetCount);
      return;
    }

    if (revealedCount >= targetCount) {
      return;
    }

    // Reveal the next row after a short beat so started → finished → todos
    // never pop in as one block.
    const id = window.setTimeout(() => {
      setRevealedCount((count) => Math.min(count + 1, targetRef.current));
    }, REVEAL_STAGGER_MS);
    return () => window.clearTimeout(id);
  }, [staggerReveal, targetCount, revealedCount]);

  useEffect(() => {
    if (revealedCount > logEntries.length) {
      setTodosRevealed(true);
    }
  }, [revealedCount, logEntries.length]);

  const visibleLogs = logEntries.slice(0, Math.min(revealedCount, logEntries.length));
  const shouldClampLogs = visibleLogs.length > maxVisibleLogRows;
  const lastVisibleLogId = visibleLogs.length > 0 ? visibleLogs[visibleLogs.length - 1]?.id : undefined;
  const showTodos =
    Boolean(todoEntry?.todos?.length) &&
    (revealedCount > logEntries.length || todosRevealed);
  const lastLogIndex = visibleLogs.length - 1;

  useLayoutEffect(() => {
    const viewport = logViewportRef.current;
    const list = logListRef.current;

    const resetViewport = () => {
      if (!viewport) return;
      viewport.style.maxHeight = '';
      viewport.classList.remove('stream-status-log-viewport--shifted');
    };

    const isNearBottom = (el: HTMLElement) => {
      const maxScroll = el.scrollHeight - el.clientHeight;
      return maxScroll - el.scrollTop <= 24;
    };

    const updateScrollFade = () => {
      if (!viewport) return;
      viewport.classList.toggle('stream-status-log-viewport--shifted', viewport.scrollTop > 4);
    };

    if (!shouldClampLogs || !viewport || !list) {
      resetViewport();
      userPinnedToBottomRef.current = true;
      return;
    }

    const syncViewport = (followLatest: boolean) => {
      const items = list.querySelectorAll<HTMLElement>('.stream-status-timeline-item');
      if (items.length <= maxVisibleLogRows) {
        resetViewport();
        return;
      }

      const startIndex = items.length - maxVisibleLogRows;
      let viewportHeight = 0;
      for (let i = startIndex; i < startIndex + maxVisibleLogRows; i++) {
        viewportHeight += items[i].offsetHeight;
      }
      viewport.style.maxHeight = `${viewportHeight}px`;

      const targetScroll = Math.max(0, viewport.scrollHeight - viewport.clientHeight);
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const shouldFollowLatest = followLatest && userPinnedToBottomRef.current;

      if (shouldFollowLatest) {
        if (prefersReducedMotion) {
          viewport.scrollTop = targetScroll;
        } else {
          viewport.scrollTo({ top: targetScroll, behavior: 'smooth' });
        }
      }

      updateScrollFade();
    };

    const handleScroll = () => {
      if (!viewport) return;
      userPinnedToBottomRef.current = isNearBottom(viewport);
      updateScrollFade();
    };

    const isNewLogRow = prevLastVisibleLogIdRef.current !== lastVisibleLogId;
    prevLastVisibleLogIdRef.current = lastVisibleLogId;

    if (isNewLogRow) {
      userPinnedToBottomRef.current = isNearBottom(viewport) || userPinnedToBottomRef.current;
    }

    syncViewport(isNewLogRow);

    viewport.addEventListener('scroll', handleScroll, { passive: true });

    const resizeObserver = new ResizeObserver(() => {
      syncViewport(userPinnedToBottomRef.current);
    });
    resizeObserver.observe(list);

    return () => {
      viewport.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
    };
  }, [shouldClampLogs, maxVisibleLogRows, visibleLogs.length, lastVisibleLogId]);

  if (!entries?.length || targetCount === 0) return null;

  const logList = (
    <ol ref={logListRef} className="stream-status-log m-0 list-none p-0">
      {visibleLogs.map((entry, index) => {
        const isLastLog = index === lastLogIndex && !showTodos;
        const connectToNext = index < lastLogIndex || showTodos;
        const isActive = shimmerActiveRow && isLastLog && !showTodos;

        const isNewestRow = index === lastLogIndex;
        const useCreditsEnter = shouldClampLogs && isNewestRow;

        return (
          <li
            key={entry.id}
            className={cn(
              'stream-status-timeline-item relative flex items-start gap-3 pb-4 last:pb-0',
              useCreditsEnter
                ? 'stream-status-timeline-item--credits-enter motion-reduce:animate-none'
                : 'animate-in fade-in slide-in-from-top-1 duration-300 fill-mode-both motion-reduce:animate-none',
              connectToNext && 'stream-status-timeline-item--connected',
            )}
          >
            <span className="stream-status-timeline-icon relative z-10 flex size-5 shrink-0 items-center justify-center">
              <StreamStatusIcon icon={entry.icon} />
            </span>
            <span
              className={cn(
                'min-w-0 pt-px text-sm font-normal leading-[160%] text-[#6D6D6D] dark:text-muted-foreground',
                isActive && 'text-shimmer',
              )}
            >
              {entry.label}
            </span>
          </li>
        );
      })}
    </ol>
  );

  return (
    <div className={cn('stream-status-timeline flex flex-col', className)}>
      {visibleLogs.length > 0 && (
        <div
          ref={logViewportRef}
          className={cn(
            'stream-status-log-viewport',
            shouldClampLogs && 'stream-status-log-viewport--clamped',
          )}
        >
          {logList}
        </div>
      )}

      {showTodos && todoEntry?.todos?.length ? (
        <div
          className={cn(
            'stream-todo-section animate-in fade-in slide-in-from-top-1 duration-300 fill-mode-both motion-reduce:animate-none',
            visibleLogs.length > 0 && 'border-[#2A2A2A] dark:border-border/50',
          )}
        >
          <StreamTodoList todos={todoEntry.todos} />
        </div>
      ) : null}
    </div>
  );
}
